'use client'

import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import { Clock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { routes } from '@/config/routes'
import { FlowBadge } from '@/features/cases/components/flow-badge'
import { CASE_STATUS_LABEL, FLOW_LABEL, type CaseRecord } from '@/features/cases/types'

/**
 * Shown on a service's own tab whenever a case exists for that flow but
 * isn't published yet — every time the investor visits, not just right
 * after submitting.
 *
 * Takes only the fields it actually renders (`Pick`, not the full `CaseRecord`) — a not-yet-
 * published case can never be fetched as full case detail (`assertCaseAccess`'s investor branch
 * requires `published`, by design: the dashboard doesn't unlock until staff reviews it), so every
 * caller here only has the case *summary* (`GET /investors/me/cases`) on hand, not a `CaseDetail`.
 */
export function PendingRequestCard({
  caseRecord,
}: {
  caseRecord: Pick<CaseRecord, 'id' | 'flow' | 'reference' | 'status'>
}) {
  const router = useRouter()

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <span className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-full">
          <Clock className="size-5" />
        </span>
        <div>
          <p className="text-base font-semibold">Request received</p>
          <p className="text-muted-foreground mx-auto mt-1 max-w-sm text-sm">
            We&apos;ve got your {FLOW_LABEL[caseRecord.flow].toLowerCase()} request — an advisor
            will call you shortly. This tab fills in once your review is published.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <FlowBadge flow={caseRecord.flow} />
          <span className="text-muted-foreground text-xs">{caseRecord.reference}</span>
          <Badge variant="secondary">{CASE_STATUS_LABEL[caseRecord.status]}</Badge>
        </div>
        {/* Scoped to this specific case (`?case=` param) — see pending-case-card.tsx's identical
            comment for why. */}
        <Button onClick={() => router.push(routes.reviewStatus(caseRecord.id) as Route)}>
          Track status
        </Button>
      </CardContent>
    </Card>
  )
}
